import React from "react";
import type { CSS } from "@stitches/react";
import { styled } from "../../../stitches.config";
import { Box } from "../../primitives";
import { BasicTooltip } from "./Tooltip";
import { AiOutlineInfoCircle } from "react-icons/ai";

export const StyledTooltip = styled(BasicTooltip, {
  width: "auto",
  maxWidth: "200px",
  padding: "10px 15px",
  textAlign: "center",
  borderRadius: "8px",
  fontFamily: "OpenSans",
  background: "#000000CB 0 0 no-repeat padding-box",
  fontSize: "13px",
  lineHeight: "17px",
  color: "white",
  variants: {
    color: {
      paleYellow: {
        border: "1px solid white",
      },
      blue: {
        border: "1px solid #08a1a9",
      },
      red: {
        border: "1px solid red",
      },
    },
  },
  "@xsRev": {
    maxWidth: "200px !important",
  },
});

type Variant = "paleYellow" | "blue" | "red";

interface InfoTooltipProps {
  children?: React.ReactNode;
  content: React.ReactNode;
  tooltipCss?: CSS;
  iconCss?: CSS;
  side?: "bottom" | "left" | "right" | "top";
  variant?: Variant;
  iconSize?: number;
  iconColor?: string;
  sideOffset?: number;
  align?: "center" | "end" | "start";
  forceClose?: boolean;
  hiddenMobile?: boolean;
}

const getIconColor = (variant: Variant): string => {
  if (variant === "paleYellow") return "#FFF084";
  if (variant === "red") return "red";
  return "#166534";
};

export const InfoTooltip: React.FC<InfoTooltipProps> = ({
  children,
  content,
  tooltipCss,
  iconCss,
  side = "top",
  variant = "blue",
  iconSize = 16,
  iconColor,
  sideOffset,
  align = "center",
  forceClose = false,
  hiddenMobile = false,
}) => {
  const color = iconColor || getIconColor(variant);

  const getChildContent = () => {
    if (children) {
      return children;
    }
    return (
      <Box css={{ cursor: "pointer", ...iconCss }}>
        <AiOutlineInfoCircle size={iconSize} color={color} />
      </Box>
    );
  };

  if (!content) {
    return <>{getChildContent()}</>;
  }

  return (
    <StyledTooltip
      color={variant}
      content={content}
      side={side}
      sideOffset={sideOffset}
      showArrow={false}
      css={tooltipCss}
      align={align}
      forceClose={forceClose}
      hiddenMobile={hiddenMobile}
    >
      {getChildContent()}
    </StyledTooltip>
  );
};
